export const ductedHero = {
  breadcrumb: "Rinnai Ducted Air Conditioning",
  eyebrow: "RINNAI DUCTED REVERSE CYCLE · PERTH WA",
  title: "Rinnai ducted air conditioning,",
  highlight: "designed around your Perth home.",
  intro: "One hidden system heats and cools your whole home, with zoning so you only condition the rooms you use. We size, design and install it around your floor plan, glazing and daily routine.",
  image: "https://cdn.magicpatterns.com/patterns/generated-images/c77a8478-080e-4c43-adbf-0892a21ee5d8.jpg",
  imageAlt: "Bright Perth living and dining area with discreet ceiling air conditioning outlets",
  facts: ["7kW to 24kW systems", "Up to 8 zones with Rinnai Home", "7 year Rinnai warranty*"],
};

export const ductedQuickAnswer = {
  question: "What is Rinnai ducted air conditioning?",
  answer: "It is a whole-home reverse cycle system. One indoor unit sits out of sight in the roof space and sends heated or cooled air through insulated ducts to discreet ceiling outlets in each room. Zoning lets you condition the whole house or only selected areas, without a wall-mounted unit in every room.",
  points: [
    { label: "System type", value: "Ducted Reverse Cycle Inverter" },
    { label: "Capacity range", value: "7kW to 24kW" },
    { label: "Zoning", value: "Up to 8 zones with Rinnai Home" },
    { label: "Operating range", value: "-20°C to +50°C outdoors" },
    { label: "Indoor noise", value: "From 28 dB(A) on selected models" },
    { label: "Warranty", value: "7 years on eligible systems*" },
  ],
};

export const ductedFeatures = [
  {
    title: "Heating and cooling in one system",
    text: "Reverse cycle comfort keeps you cool through Perth summers and warm on winter mornings, from a single system you control in one place.",
  },
  {
    title: "DC inverter efficiency",
    text: "A Direct Current inverter compressor adjusts output precisely. Rinnai rates a typical output of around 3kW of heating or cooling for every 1kW of electricity used.",
  },
  {
    title: "Quiet and out of sight",
    text: "The indoor unit sits in the roof space, leaving only slimline ceiling diffusers. Indoor noise starts from 28 dB(A) on selected models.",
  },
  {
    title: "Dehumidification mode",
    text: "Manages humidity on sticky summer days, so rooms feel comfortable without over-cooling.",
  },
  {
    title: "Automatic restart",
    text: "If the power drops out, the system restores operation automatically, so you come home to the comfort you set.",
  },
  {
    title: "Splittable for tight roof spaces",
    text: "The 15kW, 18kW, 20kW and 24kW indoor units can be carried into the roof in sections, which makes many retrofits simpler.",
  },
];

export const ductedZoning = {
  eyebrow: "RINNAI HOME CONTROLLER",
  title: "Comfort exactly where you want it.",
  text: "The Rinnai Home Controller gives each zone its own temperature. Wireless sensors track real-time conditions and redirect airflow, so bedrooms, living areas and studies stay comfortable without conditioning empty rooms.",
  points: [
    "8-inch whole-of-home touchscreen",
    "Up to 8 individual zones",
    "Active Temperature Management",
    "Fan Auto Control (installer configured)",
    "Up to 9 favourite programs",
    "Wi-Fi with Google Home, Alexa and Siri*",
  ],
  image: "https://cdn.magicpatterns.com/patterns/generated-images/97c911de-7bfa-4645-a36f-1562158c5b49.jpg",
  imageAlt: "Smartphone showing room-by-room climate control in a bright living room",
  note: "*Wi-Fi Control is available on compatible controllers only and depends on controller capability.",
  cta: { label: "Need more than 8 zones? Compare iZone", to: "/izone-smart-air-conditioning-perth" },
};

export const ductedPerth = {
  eyebrow: "WHY DUCTED SUITS PERTH",
  title: "Built for hot afternoons and cool winter mornings.",
  text: "Perth homes face long summers, strong westerly sun on large glass areas and surprisingly cold winter nights. A correctly zoned Rinnai ducted system cools living areas in the afternoon, switches to bedrooms at night and heats the whole home in winter.",
  points: [
    "Rated to operate up to +50°C outdoors",
    "Dehumidification for muggy days",
    "Automatic restart after power outages",
    "Suits new builds and existing homes",
  ],
  image: "https://cdn.magicpatterns.com/patterns/generated-images/6d0072cf-1c17-4313-a0df-38bc8cdcd47e.jpg",
  imageAlt: "Contemporary single-storey Perth home in late afternoon sun",
};

export const ductedProsCons = {
  benefits: [
    "Heats and cools the whole home from a single system",
    "Delivers consistent temperatures from room to room",
    "Discreet ceiling outlets instead of wall-mounted units",
    "Zoning conditions different areas independently",
    "Quiet, with most equipment out of sight",
    "Can improve energy efficiency when zoning is used well",
  ],
  considerations: [
    "Higher upfront cost than a split system",
    "Needs sufficient roof or ceiling space for ductwork",
    "Professional design and installation are essential",
    "Running costs depend on home size, insulation and usage",
    "Regular maintenance keeps performance and efficiency up",
    "Retrofits can involve additional installation work",
  ],
};

export const ductedSteps = [
  {
    title: "Site assessment",
    text: "We look at your floor plan, roof space, orientation, glazing and how each room is used.",
  },
  {
    title: "Heat load calculation",
    text: "We calculate what each area needs, so the system is sized to your home rather than guessed.",
  },
  {
    title: "Duct and zone design",
    text: "Outlet positions, duct runs, zones and controller placement are planned around your routine.",
  },
  {
    title: "Installation and handover",
    text: "ARC Tick licensed technicians install and commission the system, then show you the controller and app.",
  },
];

export const ductedSpecs = {
  columns: ["System", "Nominal capacity", "Rated cooling", "Rated heating", "Refrigerant", "Outdoor power"],
  rows: [
    ["07L", "7kW", "7.3kW", "7.5kW", "R32", "Single phase"],
    ["09L", "9kW", "9.0kW", "10.5kW", "R32", "Single phase"],
    ["11L", "11kW", "10.5kW", "12.5kW", "R32", "Single phase"],
    ["13L", "13kW", "12.6kW", "14.5kW", "R32", "Single phase"],
    ["15L", "15kW", "14.0kW", "16.8kW", "R32", "Single phase"],
    ["15T", "15kW", "14.0kW", "17.0kW", "R32", "Single phase"],
    ["18T", "18kW", "17.0kW", "18.0kW", "R32", "Single phase"],
    ["20", "20kW", "20.0kW", "20.0kW", "R410A", "Three phase"],
    ["24", "24kW", "24.0kW", "26.0kW", "R410A", "Three phase"],
  ],
};
